import { Box, Button, Container, Grid, Typography } from "@mui/material"
import { useApiDelete, useApiPost, useApiPut } from "auth/useApi2"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useState } from "react"
import { VehicleAlarmNotificationBar } from "./components/VehicleAlarmNotificationBar"
import { useTranslation } from "react-i18next"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"

export const alarmType = (value, t) => {
  switch (value) {
    case 1:
      return t("vehiclealarm.alcohol_lock", "Alcohol lock")
    default:
      return t("vehiclealarm.drivers_cards", "Drivers cards")
  }
}
const VehicleAlarmSettings = () => {
  const { t } = useTranslation()
  const loadingContext = useContext(GlobalLoaderContext)
  const appContext = useContext(AppContext)

  const [formData, setFormData] = useState()
  const [editable, setEditable] = useState(false)
  const [result, setResult] = useState(null)

  const [vehicleAlarmNotificationsResult, vehiclesAlarmNotificationFuncs] = useApi([])
  const [updateVehicleAlarmNotificationResult, updateVehicleAlarmNotification] = useApiPut()
  const [createVehicleAlarmNotificationResult, createVehicleAlarmNotification] = useApiPost()
  const [deleteResult, deleteNotification, resetDeleteState] = useApiDelete()

  useEffect(() => {
    vehiclesAlarmNotificationFuncs.get(
      apiEndpoints.vehicleregistryadmin.getvehiclealarmnotificationsbycustomerid
    )
    return () => {
      vehiclesAlarmNotificationFuncs.leave()
    }
  }, [vehiclesAlarmNotificationFuncs])

  useEffect(() => {
    loadingContext.setLoading(() => vehicleAlarmNotificationsResult.status === 1)
  })

  const getFormData = (row) => {
    setEditable(true)
    setFormData(row)
    appContext.toggleSideBar(true)
  }
  const getEmptyFormData = (row) => {
    setEditable(false)
    setFormData(row)
    appContext.toggleSideBar(true)
  }

  const feedback = {
    errorTitle: "Notifikation kunde inte skapas"
  }

  const handleSubmit = (values) => {
    if (editable) {
      updateVehicleAlarmNotification(apiEndpoints.vehicleregistryadmin.updatevehiclealarmnotification, values)
    } else {
      createVehicleAlarmNotification(apiEndpoints.vehicleregistryadmin.createvehiclealarmnotification, values)
    }
  }

  useEffect(() => {
    setResult(updateVehicleAlarmNotificationResult)
    if (updateVehicleAlarmNotificationResult.status === 2) {
      vehiclesAlarmNotificationFuncs.get(
        apiEndpoints.vehicleregistryadmin.getvehiclealarmnotificationsbycustomerid
      )
      appContext.toggleSideBar(false)
      setFormData(null)
    }
  }, [updateVehicleAlarmNotificationResult, vehiclesAlarmNotificationFuncs])

  useEffect(() => {
    setResult(createVehicleAlarmNotificationResult)
    if (createVehicleAlarmNotificationResult.status === 2) {
      vehiclesAlarmNotificationFuncs.get(
        apiEndpoints.vehicleregistryadmin.getvehiclealarmnotificationsbycustomerid
      )
      appContext.toggleSideBar(false)
      setFormData(null)
    }
  }, [createVehicleAlarmNotificationResult, vehiclesAlarmNotificationFuncs])

  const handleDelete = (id) => {
    deleteNotification(apiEndpoints.vehicleregistryadmin.deletevehiclealarmnotification.replace("{id}", id))
  }

  useEffect(() => {
    setResult(deleteResult)

    if (deleteResult.status === 2) {
      vehiclesAlarmNotificationFuncs.get(
        apiEndpoints.vehicleregistryadmin.getvehiclealarmnotificationsbycustomerid
      )
      appContext.toggleSideBar(false)

      resetDeleteState()
    }
  }, [deleteResult, resetDeleteState, vehiclesAlarmNotificationFuncs])

  const columns = [
    {
      accessorFn: (row) => row.alarmType,
      id: "alarmType",
      cell: ({ cell }) => {
        return alarmType(cell.row.original.alarmType, t)
      },
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      header: t("vehiclealarm.alarm_type", "Alarm type"),
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter,
        filterHeader: t("vehiclealarm.alarm_type", "Alarm type"),
        filterOptions: [
          {
            name: t("vehiclealarm.alcohol_lock", "Alcohol lock"),
            value: 1
          },
          {
            name: t("vehiclealarm.drivers_cards", "Drivers card"),
            value: 2
          }
        ]
      }
    },
    {
      accessorKey: "user.name",
      header: t("global.name", "Name")
    },
    {
      accessorKey: "user.email",
      header: t("global.email", "Email")
    },
    {
      accessorKey: "company.name",
      header: t("global.company", "Company")
    },
    {
      accessorKey: "edit",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => {
                getFormData(cell.row.original)
              }}
            />
          </StyledTableButtonGroup>
        )
      }
    }
  ]
  return (
    <div>
      <VehicleAlarmNotificationBar
        formData={formData}
        onSubmit={handleSubmit}
        editVehicleAlarmNotification={editable}
        result={result}
        feedback={feedback}
        deleteNotificationSubmit={handleDelete}
        deleteResult={deleteResult}
      />

      <Container>
        <Box>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h1">{t("vehiclealarm.alarm_settings", "Alarm settings")}</Typography>
            <Button color="primary" variant="contained" onClick={getEmptyFormData}>
              {t("global.add", "Add")}
            </Button>
          </Grid>
        </Box>
      </Container>
      <Box bgcolor="background.paper" borderRadius="8px">
        <ClpTable
          columns={columns}
          onRowClick={(row) => getFormData(row.original)}
          rows={vehicleAlarmNotificationsResult.status === 2 && vehicleAlarmNotificationsResult.data}
          loadingState={vehicleAlarmNotificationsResult.status}
        />
      </Box>
    </div>
  )
}
export default VehicleAlarmSettings
